import { useState, useEffect } from 'react';
import { getAffiliateStatisticsWithComparison, getAffiliateStatisticsForPeriod } from '../services/affiliateService';

// Define time period types
export type TimePeriod = 'all_time' | 'today' | 'this_week' | 'this_month' | 'this_quarter' | 'this_year';

// Define the affiliate statistics type
export interface AffiliateStatistics {
  totalAffiliates: number;
  pendingApplications: number;
  approvedAffiliates: number;
  totalCommissionsPaid: number;
  newAffiliatesThisMonth: number;
  monthlyCommissions: number;
}

// Define affiliate monthly snapshot type
export interface AffiliateMonthlySnapshot {
  totalAffiliates: number;
  pendingApplications: number;
  approvedAffiliates: number;
  totalCommissionsPaid: number;
  newAffiliatesThisMonth: number;
  monthlyCommissions: number;
}

export interface UseAffiliateStatisticsOptions {
  timePeriod?: TimePeriod;
}

export interface UseAffiliateStatisticsResult {
  statistics: AffiliateStatistics | null;
  previousSnapshot: AffiliateMonthlySnapshot | null;
  percentageChanges: {
    totalAffiliates: number;
    pendingApplications: number;
    approvedAffiliates: number;
    totalCommissionsPaid: number;
    newAffiliatesThisMonth: number;
    monthlyCommissions: number;
  } | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export const useAffiliateStatistics = (options: UseAffiliateStatisticsOptions = {}): UseAffiliateStatisticsResult => {
  const { timePeriod = 'all_time' } = options;
  
  const [statistics, setStatistics] = useState<AffiliateStatistics | null>(null);
  const [previousSnapshot, setPreviousSnapshot] = useState<AffiliateMonthlySnapshot | null>(null);
  const [percentageChanges, setPercentageChanges] = useState<{
    totalAffiliates: number;
    pendingApplications: number;
    approvedAffiliates: number;
    totalCommissionsPaid: number;
    newAffiliatesThisMonth: number;
    monthlyCommissions: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (timePeriod === 'all_time') {
        // Use the original function for all time stats
        const result = await getAffiliateStatisticsWithComparison();
        setStatistics(result.current);
        setPreviousSnapshot(result.previous);
        setPercentageChanges(result.percentageChanges);
      } else {
        // Use the new function for time period filtered stats
        const result = await getAffiliateStatisticsForPeriod(timePeriod);
        setStatistics(result.current);
        setPreviousSnapshot(result.previous);
        setPercentageChanges(result.percentageChanges);
      }
    } catch (err) {
      console.error('Error fetching affiliate statistics:', err);
      setError('Failed to load affiliate statistics');
      setStatistics(null);
      setPreviousSnapshot(null);
      setPercentageChanges(null);
    } finally {
      setLoading(false);
    }
  };

  const refresh = async () => {
    await fetchStatistics();
  };

  useEffect(() => {
    fetchStatistics();
  }, [timePeriod]);

  return {
    statistics,
    previousSnapshot,
    percentageChanges,
    loading,
    error,
    refresh
  };
}; 